import { createContext, useContext } from "react";

import type { IWidgetDefinition, TResizeAxis, TWidgetSizeKey } from "./types";

/**
 * What a widget frame can do to itself.
 *
 * These travel by context rather than by prop for one concrete reason: bound as
 * props they had to be re-created per widget on every render of the grid
 * (`onRemove={() => remove(id)}`), which handed every frame four fresh function
 * identities each time anything on the canvas changed and made `React.memo`
 * useless. Passing the id at call time instead keeps this object referentially
 * stable for the life of the canvas, so a frame only re-renders when its own
 * data changes — during a drag that is one frame instead of all of them.
 */
export interface ICanvasActions {
  remove(instanceId: string): void;
  resize(instanceId: string, size: TWidgetSizeKey): void;
  /** Move one notch along the widget's allowed size scale. */
  step(
    instanceId: string,
    definition: IWidgetDefinition,
    current: TWidgetSizeKey,
    direction: 1 | -1,
  ): void;
  beginResize(
    event: React.PointerEvent<HTMLElement>,
    instanceId: string,
    definition: IWidgetDefinition,
    current: TWidgetSizeKey,
    axis: TResizeAxis,
  ): void;
}

export const CanvasActionsContext = createContext<ICanvasActions | null>(null);

export function useCanvasActions(): ICanvasActions {
  const actions = useContext(CanvasActionsContext);
  if (!actions) {
    throw new Error("useCanvasActions must be used inside a DashboardGrid");
  }
  return actions;
}
